package com.mgacreative.hotelru.domain.repository

import com.mgacreative.hotelru.core.network.SupabaseNetworkClient
import com.mgacreative.hotelru.core.tenant.TenantSession
import com.mgacreative.hotelru.domain.model.CashMovement
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.Serializable

@Serializable
data class CurrentAccount(
    val id: String = "",
    val tenant_id: String = "",
    val name: String,
    val type: String, // SUPPLIER, CUSTOMER
    val balance: Double = 0.0
)

interface FinanceRepository {
    fun observeCashMovements(): Flow<List<CashMovement>>
    fun observeCurrentAccounts(): Flow<List<CurrentAccount>>
    suspend fun loadCashMovements()
    suspend fun loadCurrentAccounts()
    suspend fun addCashMovement(type: String, amount: Double, description: String, date: String)
    suspend fun addCurrentAccount(name: String, type: String, balance: Double)
}

class FinanceRepositoryImpl(
    private val networkClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession
) : FinanceRepository {

    private val _movements = MutableStateFlow<List<CashMovement>>(emptyList())
    private val _accounts = MutableStateFlow<List<CurrentAccount>>(emptyList())

    override fun observeCashMovements(): Flow<List<CashMovement>> = _movements.asStateFlow()
    override fun observeCurrentAccounts(): Flow<List<CurrentAccount>> = _accounts.asStateFlow()

    override suspend fun loadCashMovements() {
        try {
            val tenantId = tenantSession.currentTenantId
            if (tenantId != null) {
                val result = networkClient.client.postgrest["cash_movements"]
                    .select {
                        filter { eq("tenant_id", tenantId) }
                    }
                    .decodeList<CashMovement>()
                    
                _movements.value = result.sortedByDescending { it.created_at }
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun loadCurrentAccounts() {
        try {
            val tenantId = tenantSession.currentTenantId
            if (tenantId != null) {
                val result = networkClient.client.postgrest["current_accounts"]
                    .select {
                        filter { eq("tenant_id", tenantId) }
                    }
                    .decodeList<CurrentAccount>()
                    
                _accounts.value = result
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun addCashMovement(type: String, amount: Double, description: String, date: String) {
        try {
            val tenantId = tenantSession.currentTenantId
            if (tenantId != null) {
                @kotlinx.serialization.Serializable
                data class MovementRequest(
                    val tenant_id: String,
                    val type: String,
                    val amount: Double,
                    val description: String,
                    val movement_date: String
                )
                
                val req = MovementRequest(tenantId, type, amount, description, date)
                networkClient.client.postgrest["cash_movements"].insert(req)
                
                // Refresh list
                loadCashMovements()
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun addCurrentAccount(name: String, type: String, balance: Double) {
        try {
            val tenantId = tenantSession.currentTenantId
            if (tenantId != null) {
                @kotlinx.serialization.Serializable
                data class AccountRequest(
                    val tenant_id: String,
                    val name: String,
                    val type: String,
                    val balance: Double
                )
                
                val req = AccountRequest(tenantId, name, type, balance)
                networkClient.client.postgrest["current_accounts"].insert(req)
                
                // Refresh list
                loadCurrentAccounts()
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }
}

